import { SchemaNode } from "./node";

// define a design transformer function
// the transformer takes in the current schema node, the current output, and the list of transformers
export type DisplayAdapter<T> = {
  label: string;
  canHandle: (node: SchemaNode) => boolean;
  render: (source: SchemaNode, adapter: DisplayAdapter<T>[]) => T[];
};

// RenderToMap returns the names of each section as a key in a dictionary, with the value being the output of the adapter
export function RenderToMap<T>(
  target: SchemaNode,
  adapters: DisplayAdapter<T>[],
): Record<string, T[]> {
  // we assume the json schema has a root node and sections, so walk those first
  if (target.label !== "root" && target.label !== "envelope") {
    throw new Error("Expected a root node or section node");
  }
  if (target.children.length === 0) {
    throw new Error("Expected a non-empty root node");
  }
  // we assume the json schema has a root node and sections, so walk those first
  if (target.label !== "root" && target.label !== "envelope") {
    throw new Error("Expected a root node or section node");
  }
  if (target.children.length === 0) {
    throw new Error("Expected a non-empty root node");
  }
  const toSearch: SchemaNode[] = [];
  for (const section of target.children) {
    for (const segment of section.children) {
      toSearch.push(segment);
    }
  }
  const results: Record<string, T[]> = {};
  for (const segment of toSearch) {
    results[segment.label ? segment.label : segment.id] = Render(
      segment,
      adapters,
    );
  }

  return results;
}

export function renderWithoutEnvelope<T>(
  target: SchemaNode,
  adapters: DisplayAdapter<T>[],
): T[] {
  // we assume the json schema has a root node and sections, so walk those first
  if (target.label !== "root" && target.label !== "envelope") {
    throw new Error("Expected a root node or section node");
  }
  if (target.children.length === 0) {
    throw new Error("Expected a non-empty root node");
  }
  const toSearch: SchemaNode[] = [];
  for (const section of target.children) {
    for (const segment of section.children) {
      toSearch.push(segment);
    }
  }
  let results: T[] = [];
  for (const segment of toSearch) {
    results = results.concat(Render(segment, adapters));
  }

  return results;
}

export function Render<T>(
  target: SchemaNode,
  adapters: DisplayAdapter<T>[],
): T[] {
  // for this node, run the adapter and alter the output
  for (const adapter of adapters) {
    // check if the adapter is actually defined
    if (adapter === null || adapter === undefined) {
      console.log("Encountered a null or undefined adapter");
      continue;
    }
    if (!adapter.canHandle(target)) {
      continue;
    }
    const result = adapter.render(target, adapters);
    if (result === null || result === undefined) {
      continue;
    }
    return result;
  }

  // NOTE: THIS INTENTIONALLY DOES NOT WALK THE CHILDREN, IT'S ASSUMED THE ADAPTERS WILL DO THAT

  return [];
}
